import type {
  EnterpriseTask,
} from "@swot/swot-components";

import {
  readBoolean,
  readDateIso,
  readString,
} from "./mapperUtils.js";

export type CmtsTaskVm =
  EnterpriseTask &
  Record<string, unknown>;

export function mapTask(
  dto: unknown,
): CmtsTaskVm {
  const assignedToUserId =
    readString(
      dto,
      [
        "assignedToUserId",
        "assigneeId",
      ],
      "",
    );

  const assignedToUserName =
    readString(
      dto,
      [
        "assignedToUserName",
        "assignedUserName",
        "assigneeName",
      ],
      "",
    );

  const ownerUserId =
    readString(
      dto,
      [
        "ownerUserId",
        "ownerId",
      ],
      "",
    );

  const ownerName =
    readString(
      dto,
      [
        "ownerName",
        "ownerDisplayName",
      ],
      "",
    );

  const linkedEntityId =
    readString(
      dto,
      [
        "linkedEntityId",
        "parentComplaintId",
        "complaintId",
        "linkedComplaintId",
        "sourceRecordId",
        "entityId",
      ],
      "",
    );

  /*
   * Preserve the original backend
   * task lifecycle status.
   *
   * Examples:
   * OPEN, ASSIGNED, ACCEPTED,
   * ACKNOWLEDGED, SNOOZED,
   * COMPLETED, REOPENED, CANCELLED.
   */
  const rawTaskStatus =
    readString(
      dto,
      [
        "status",
        "statusCode",
        "taskStatus",
      ],
      "OPEN",
    )
      .trim()
      .toUpperCase();

  const rawPriority =
    readString(
      dto,
      [
        "priorityCode",
        "priority",
      ],
      "MEDIUM",
    )
      .trim()
      .toUpperCase();

  return {
    id: readString(
      dto,
      [
        "id",
        "taskId",
      ],
      "",
    ),

    taskId: readString(
      dto,
      [
        "taskId",
        "id",
      ],
      "",
    ),

    title: readString(
      dto,
      [
        "title",
        "subject",
        "summary",
      ],
      "CMTS task",
    ),

    /*
     * EnterpriseTask-compatible
     * normalized status.
     */
    status:
      normalizeTaskStatus(
        rawTaskStatus,
      ),

    /*
     * Exact backend status used by
     * CmtsTasksPage status chip.
     */
    taskStatus:
      rawTaskStatus,

    /*
     * EnterpriseTask-compatible
     * normalized priority.
     */
    priority:
      normalizePriority(
        rawPriority,
      ),

    /*
     * Exact backend priority code.
     */
    priorityCode:
      rawPriority,

    /*
     * Assigned user details.
     */
    assignedToUserId:
      assignedToUserId ||
      undefined,

    assignedToUserName:
      assignedToUserName ||
      undefined,

    /*
     * Task owner details.
     */
    ownerId:
      ownerName ||
      ownerUserId ||
      assignedToUserName ||
      assignedToUserId ||
      "Unassigned",

    ownerUserId:
      ownerUserId ||
      assignedToUserId ||
      "unassigned",

    ownerName:
      ownerName ||
      assignedToUserName ||
      "Unassigned",

    dueAt: readDateIso(
      dto,
      [
        "dueAt",
        "dueAtUtc",
        "dueDate",
        "targetDate",
        "targetDateUtc",
        "dueDateUtc",
        "slaDueUtc",
        "deadline",
        "scheduledAtUtc",
      ],
      "",
    ),

    complaintId:
      readString(
        dto,
        [
          "complaintId",
          "parentComplaintId",
          "linkedComplaintId",
          "linkedEntityId",
          "sourceRecordId",
          "entityId",
        ],
        "",
      ),

    linkedEntityId,

    linkedEntityType:
      readString(
        dto,
        [
          "linkedEntityType",
          "sourceModule",
          "entityType",
        ],
        linkedEntityId
          ? "Complaint"
          : "General",
      ),

    linkedRecordCode:
      readString(
        dto,
        [
          "linkedRecordCode",
          "linkedEntityCode",
          "linkedEntityNumber",
          "sourceDisplayCode",
          "displayCode",
          "complaintNumber",
          "complaintCode",
          "referenceNumber",
          "sourceReferenceNumber",
          "taskNumber",
        ],
        "",
      ),

    sourceLinks:
      Array.isArray((dto as Record<string, unknown>)?.sourceLinks)
        ? (dto as Record<string, unknown>).sourceLinks
        : undefined,

  

    createdAt:
      readDateIso(
        dto,
        [
          "createdAt",
          "createdAtUtc",
        ],
        "",
      ),

    completedAt:
      readDateIso(
        dto,
        [
          "completedAt",
          "completedAtUtc",
        ],
        "",
      ),

    canAccept:
      readBoolean(
        dto,
        ["canAccept"],
        false,
      ),

    canAcknowledge:
      readBoolean(
        dto,
        ["canAcknowledge"],
        false,
      ),

    canAssign:
      readBoolean(
        dto,
        ["canAssign"],
        false,
      ),

    canComplete:
      readBoolean(
        dto,
        ["canComplete"],
        false,
      ),

    canSnooze:
      readBoolean(
        dto,
        ["canSnooze"],
        false,
      ),

    canReopen:
      readBoolean(
        dto,
        ["canReopen"],
        false,
      ),

    canCancel:
      readBoolean(
        dto,
        ["canCancel"],
        false,
      ),
  };
}

function normalizeTaskStatus(
  value: string,
): EnterpriseTask["status"] {
  const normalizedValue =
    value
      .trim()
      .toLowerCase();

  if (
    normalizedValue ===
      "done" ||
    normalizedValue ===
      "completed" ||
    normalizedValue ===
      "closed"
  ) {
    return "done";
  }

  if (
    normalizedValue ===
    "blocked"
  ) {
    return "blocked";
  }

  if (
    normalizedValue ===
      "cancelled" ||
    normalizedValue ===
      "canceled"
  ) {
    return "cancelled";
  }

  if (
    normalizedValue ===
      "in_progress" ||
    normalizedValue ===
      "in-progress" ||
    normalizedValue ===
      "accepted" ||
    normalizedValue ===
      "acknowledged" ||
    normalizedValue ===
      "assigned" ||
    normalizedValue ===
      "reopened"
  ) {
    return "in_progress";
  }

  /*
   * EnterpriseTask type does not
   * support all backend lifecycle
   * values. The exact status remains
   * available in taskStatus.
   */
  return "open";
}

function normalizePriority(
  value: string,
): EnterpriseTask["priority"] {
  const normalizedValue =
    value
      .trim()
      .toLowerCase();

  if (
    normalizedValue ===
      "low" ||
    normalizedValue ===
      "high" ||
    normalizedValue ===
      "critical"
  ) {
    return normalizedValue;
  }

  return "medium";
}